/* eslint-disable fp/no-mutation */
import React, { useState, useRef } from 'react'
import { Box } from '@mui/material'
import { ImageElement } from './types'
import { CANVAS_WIDTH, CANVAS_HEIGHT, PROTECTED_ZONES } from './constants'

type DraggableImageProps = {
  element: ImageElement
  scale: number
  isSelected: boolean
  onUpdate: (updates: Partial<ImageElement>) => void
  onSelect: () => void
}

export default ({
  element,
  scale,
  isSelected,
  onUpdate,
  onSelect,
}: DraggableImageProps) => {
  let [isDragging, setIsDragging] = useState(false)
  let [isResizing, setIsResizing] = useState(false)
  let [dragStart, setDragStart] = useState({
    x: 0,
    y: 0,
    elementX: 0,
    elementY: 0,
  })
  let [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    elementWidth: 0,
    elementHeight: 0,
  })
  let dragRef = useRef<HTMLDivElement>(null)

  let handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onSelect()

    if ((e.target as HTMLElement).classList.contains('resize-handle')) {
      setIsResizing(true)
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        elementWidth: element.width,
        elementHeight: element.height,
      })
    } else {
      setIsDragging(true)
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        elementX: element.x,
        elementY: element.y,
      })
    }
  }

  let handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      let deltaX = (e.clientX - dragStart.x) / scale
      let deltaY = (e.clientY - dragStart.y) / scale

      let newX = Math.max(
        0,
        Math.min(CANVAS_WIDTH - element.width, dragStart.elementX + deltaX),
      )
      let newY = Math.max(
        0,
        Math.min(CANVAS_HEIGHT - element.height, dragStart.elementY + deltaY),
      )

      // Check if new position overlaps with protected zones
      let overlapsProtected = PROTECTED_ZONES.some(
        (zone) =>
          newX < zone.x + zone.width &&
          newX + element.width > zone.x &&
          newY < zone.y + zone.height &&
          newY + element.height > zone.y,
      )

      if (!overlapsProtected) {
        onUpdate({ x: newX, y: newY })
      }
    } else if (isResizing) {
      let deltaX = (e.clientX - resizeStart.x) / scale

      let aspectRatio = element.originalWidth / element.originalHeight
      let newWidth = Math.max(50, resizeStart.elementWidth + deltaX)
      let newHeight = newWidth / aspectRatio

      // Ensure image doesn't go outside canvas bounds
      newWidth = Math.min(newWidth, CANVAS_WIDTH - element.x)
      newHeight = Math.min(newHeight, CANVAS_HEIGHT - element.y)

      // Maintain aspect ratio
      if (newWidth / aspectRatio > newHeight) {
        newWidth = newHeight * aspectRatio
      } else {
        newHeight = newWidth / aspectRatio
      }

      onUpdate({ width: newWidth, height: newHeight })
    }
  }

  let handleMouseUp = () => {
    setIsDragging(false)
    setIsResizing(false)
  }

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
    return undefined
  }, [isDragging, isResizing, dragStart, resizeStart, element, scale])

  return (
    <Box
      ref={dragRef}
      sx={{
        position: 'absolute',
        left: element.x * scale,
        top: element.y * scale,
        width: element.width * scale,
        height: element.height * scale,
        cursor: isDragging ? 'grabbing' : 'grab',
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'transparent',
        borderStyle: 'solid',
        '&:hover': {
          borderColor: isSelected ? 'primary.main' : 'primary.light',
        },
      }}
      onMouseDown={handleMouseDown}
    >
      <Box
        component="img"
        src={element.dataUrl}
        alt="Overlay"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />

      {isSelected && (
        <Box
          className="resize-handle"
          sx={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            width: 12,
            height: 12,
            bgcolor: 'primary.main',
            cursor: 'se-resize',
            borderRadius: '50%',
            border: 2,
            borderColor: 'background.paper',
          }}
        />
      )}
    </Box>
  )
}
